/**
 * <s-countdown> — Live countdown timer.
 *
 * Element dataset:
 *   data-end          — ISO 8601 end time (e.g. 2026-11-08T23:59:00+0800).
 *   data-hide-on-end  — When present, hides the closest [data-countdown-bar]
 *                       ancestor once the countdown reaches zero.
 *
 * Required child markup:
 *   [data-unit="days|hours|minutes|seconds"] — elements whose text is the two-digit value.
 */
class SCountdown extends HTMLElement {
  #timer;
  #end = NaN;
  #units = {};

  connectedCallback() {
    const raw = this.dataset.end || '';
    // Normalize "+0800" offsets to "+08:00" for consistent Date parsing.
    this.#end = Date.parse(raw.replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));
    this.querySelectorAll('[data-unit]').forEach((el) => {
      this.#units[el.dataset.unit] = el;
    });
    this.#tick();
    this.#timer = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  #tick() {
    const remaining = Number.isNaN(this.#end) ? 0 : Math.max(0, Math.floor((this.#end - Date.now()) / 1000));
    const values = {
      days: Math.floor(remaining / 86400),
      hours: Math.floor((remaining % 86400) / 3600),
      minutes: Math.floor((remaining % 3600) / 60),
      seconds: remaining % 60,
    };
    for (const [key, el] of Object.entries(this.#units)) {
      const text = String(values[key] ?? 0).padStart(2, '0');
      if (el.textContent !== text) el.textContent = text;
    }
    if (remaining === 0) {
      clearInterval(this.#timer);
      this.toggleAttribute('data-ended', true);
      if (this.hasAttribute('data-hide-on-end')) {
        const bar = this.closest('[data-countdown-bar]');
        if (bar) bar.hidden = true;
      }
    }
  }
}

if (!customElements.get('s-countdown')) customElements.define('s-countdown', SCountdown);
